import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import toast from 'react-hot-toast';
import {
  RosEditData,
  RosReportType,
  useGetReportOfSaleEditDataQuery,
  useSaveCustomReportOfSaleMutation,
  useDownloadReportOfSaleMutation,
} from '../../../../services/api/report-of-sale';
import SendReportOfSaleModal from './SendReportOfSaleModal';

const TavirajFont = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Taviraj:wght@400;700&display=swap');
`;

const FullScreen = styled.div`
  position: fixed;
  inset: 0;
  background: #fff;
  z-index: 1400;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const CloseIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: #225a91;
  line-height: 0;
  &:hover {
    opacity: 0.7;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  width: 116px;
  height: 44px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  font-size: 16px;
  box-shadow: 0px 4px 4px 0px #00000040;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendButton = styled(ActionButton)`
  background: #3994de66;
  color: #225a91;
`;

const EditButton = styled(ActionButton)`
  background: #225a91;
  color: #fff;
`;

const SaveButton = styled(ActionButton)`
  background: #3994de;
  color: #fff;
`;

const DownloadButton = styled(ActionButton)`
  width: 44px;
  background: #e5e5e5;
  color: #225a91;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px 16px;
`;

const ModalContainer = styled.div`
  background: #fff;
  width: 760px;
  max-width: 100%;
  border-radius: 4px;
  font-family: 'Taviraj', sans-serif;
  position: relative;
`;

const ModalBody = styled.div`
  padding: 12px 20px;
`;

const FormFieldset = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
  &:disabled input,
  &:disabled textarea {
    color: #000;
    -webkit-text-fill-color: #000;
    opacity: 1;
    cursor: default;
  }
`;

const RosHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const RosHeaderLeft = styled.div`
  font-size: 12px;
  line-height: 1.4;
  h1 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 2px;
  }
  p {
    margin: 0;
  }
`;

const RosDivider = styled.hr`
  border: none;
  border-bottom: 1px solid #f6f6f6;
  margin: 6px 0;
`;

const RosTitleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const AuctionIdField = styled.div`
  color: #679c37;
  font-size: 16px;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RosTitle = styled.div`
  flex: 1;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
`;

const RosTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #222;
`;

const RosHeaderRow = styled.tr`
  background: #eee;
  td {
    padding: 3px 4px;
    font-size: 14px;
    font-weight: 700;
    border: 1px solid #222;
  }
  /* Beat the global tr > td:last-child { padding: 12px !important } on specificity. */
  td:last-child {
    padding: 3px 4px !important;
  }
`;

const RosRow = styled.tr`
  td {
    padding: 2px 4px;
    border: 1px solid #222;
    font-size: 14px;
    vertical-align: top;
  }
  /* A global rule "tr > td:last-child { padding: 12px !important }" pushes the
     last cell's content down and breaks row alignment. Both are !important, so
     specificity wins it: "td:last-child" here (0,2,1) beats the global (0,1,2). */
  td:last-child {
    width: 50%;
    padding: 2px 4px !important;
  }
  td.small {
    width: 40px;
  }
  td.small-text {
    font-size: 12px;
  }
`;

const FieldLabel = styled.span`
  font-size: 12px;
  display: block;
  color: #555;
  height: 16px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FieldInput = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px dashed #bbb;
  background: transparent;
  font-family: 'Taviraj', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #000;
  padding: 0;
  line-height: 1.3;
  outline: none;
  &:focus {
    border-bottom-color: #679c37;
    background: #f9fff5;
  }
  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const TwoUp = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  > div {
    flex: 1;
    min-width: 0;
  }
`;

const FeeBox = styled.div`
  font-size: 16px;
  width: 70%;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
  clear: both;
`;

const FeeTotalRow = styled(FeeRow)`
  border-top: 1px solid #000;
  color: #679c37;
  font-weight: 600;
`;

const FeeLabelSpan = styled.span``;

const FeeValueInput = styled(FieldInput)`
  text-align: right;
  width: 100px;
`;

const PurchasePriceCell = styled.td`
  color: #679c37;
  font-size: 16px;
  border: 1px solid #222;
  padding: 2px 4px;
`;

const PickupLabel = styled.p`
  font-size: 12px;
  font-family: 'Taviraj', sans-serif;
  margin: 5px 0 0;
  font-weight: 700;
`;

const PickupTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px dashed #bbb;
  background: transparent;
  font-family: 'Taviraj', sans-serif;
  font-size: 12px;
  color: #000;
  padding: 0;
  resize: vertical;
  min-height: 36px;
  outline: none;
  &:focus {
    border-bottom-color: #679c37;
    background: #f9fff5;
  }
`;

const BuyerFeeNote = styled.div`
  margin-top: 1px;
  font-size: 12px;
  text-align: right;
  font-family: 'Taviraj', sans-serif;
`;

const StatusMessage = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: 14px;
  color: #666;
`;

const parseMoney = (v?: string): number => parseFloat((v || '').replace(/[$,]/g, '') || '0') || 0;

const formatMoney = (n: number): string =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Props = {
  vehicleId: number;
  reportType: RosReportType;
  versionId: number;
  onClose: () => void;
};

const EditReportOfSaleModal = ({ vehicleId, reportType, versionId, onClose }: Props) => {
  const { data, isFetching, isError } = useGetReportOfSaleEditDataQuery({ vehicleId, reportType, versionId });
  const [saveCustom, { isLoading: saving }] = useSaveCustomReportOfSaleMutation();
  const [downloadReport, { isLoading: downloading }] = useDownloadReportOfSaleMutation();

  const [fields, setFields] = useState<RosEditData | null>(null);
  const [totalOverridden, setTotalOverridden] = useState(false);
  const [editable, setEditable] = useState(false);
  const [sending, setSending] = useState(false);

  // The version that Send currently reflects — starts as the opened version,
  // and advances to the newly created version whenever the user saves.
  const [currentVersionId, setCurrentVersionId] = useState(versionId);

  useEffect(() => {
    if (data) {
      setFields(data);
      setTotalOverridden(false);
    }
  }, [data]);

  const setField = <K extends keyof RosEditData>(key: K, val: RosEditData[K]) =>
    setFields(f => (f ? { ...f, [key]: val } : f));

  const setVehicle = (key: keyof RosEditData['vehicle'], val: string) =>
    setFields(f => (f ? { ...f, vehicle: { ...f.vehicle, [key]: val } } : f));

  const setSeller = (key: keyof RosEditData['seller'], val: string) =>
    setFields(f => (f ? { ...f, seller: { ...f.seller, [key]: val } } : f));

  const setBuyer = (key: keyof RosEditData['buyer'], val: string) =>
    setFields(f => (f ? { ...f, buyer: { ...f.buyer, [key]: val } } : f));

  const recalcBuyerTotal = () => {
    if (totalOverridden) return;
    setFields(f => {
      if (!f) return f;
      const total =
        parseMoney(f.vehicle.price) +
        parseMoney(f.processing_fee) +
        parseMoney(f.transportation_fee) +
        parseMoney(f.additional_fees_total);
      return { ...f, vehicle_with_buy_fee: formatMoney(total) };
    });
  };

  const recalcSellerTotal = () => {
    if (totalOverridden) return;
    setFields(f => {
      if (!f) return f;
      const total =
        parseMoney(f.vehicle.price) - parseMoney(f.seller.fee) - parseMoney(f.seller.additional_fees_total);
      return { ...f, vehicle: { ...f.vehicle, totalWithoutFee: formatMoney(total) } };
    });
  };

  const handleSave = async () => {
    if (!fields) return;
    try {
      const result = await saveCustom({ vehicleId, reportType, payload: fields }).unwrap();
      setCurrentVersionId(result.id);
      setEditable(false);
      toast.success('Report of sale saved.');
    } catch {
      toast.error('Failed to save report of sale.');
    }
  };

  const handleDownload = async () => {
    try {
      const url = await downloadReport({ vehicleId, reportType, versionId: currentVersionId }).unwrap();
      // The signed URL carries Content-Disposition: attachment, so this saves rather than navigates.
      const a = document.createElement('a');
      a.href = url;
      a.click();
    } catch {
      toast.error('Failed to download report of sale.');
    }
  };

  const showSellerFees = reportType === 'seller' && !!fields?.seller.fee;
  const showBuyerFees = reportType === 'buyer' && !fields?.buyer_fee;
  const showPurchasePriceOnly =
    !!fields && ((!fields.seller?.fee && reportType !== 'buyer') || !!fields.buyer_fee);

  return createPortal(
    <FullScreen>
      <Toolbar>
        <CloseIconButton onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseIconButton>
        <Actions>
          <SendButton type="button" onClick={() => setSending(true)} disabled={isFetching || isError || !fields}>
            Send
          </SendButton>
          <EditButton type="button" onClick={() => setEditable(true)} disabled={editable}>
            Edit
          </EditButton>
          <SaveButton
            type="button"
            onClick={handleSave}
            disabled={!editable || saving || isFetching || isError || !fields}
          >
            {saving ? 'Saving…' : 'Save'}
          </SaveButton>
          <DownloadButton
            type="button"
            onClick={handleDownload}
            disabled={downloading || isFetching || isError || !fields}
            aria-label="Download"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </DownloadButton>
        </Actions>
      </Toolbar>

      <ScrollWrap>
        <ModalContainer>
          <ModalBody>
            <TavirajFont />

            {isFetching && <StatusMessage>Loading…</StatusMessage>}
            {isError && <StatusMessage>Failed to load report data.</StatusMessage>}

            {!isFetching && !isError && fields && (
              <FormFieldset disabled={!editable}>
              <RosHeader>
                <RosHeaderLeft>
                  <h1>AutoAxess.com</h1>
                  <p>486 S. K Street</p>
                  <p>Livermore, CA 94550</p>
                  <p>E-mail: info@autoaxess.com</p>
                  <p>Contact: 925-315-4033</p>
                  <p>URL: www.autoaxess.com</p>
                </RosHeaderLeft>
              </RosHeader>
              <RosDivider />

              <RosTitleRow>
                <AuctionIdField>
                  Auction ID:&nbsp;
                  <FieldInput
                    value={fields.auction_id}
                    onChange={e => setField('auction_id', e.target.value)}
                    style={{ width: 140, display: 'inline-block' }}
                  />
                </AuctionIdField>
                <RosTitle>
                  Wholesale
                  <br />
                  Report of Sale
                </RosTitle>
                <div style={{ flex: 1 }} />
              </RosTitleRow>

              <RosTable>
                <tbody>
                  <RosHeaderRow>
                    <td colSpan={3}>Vehicle information</td>
                  </RosHeaderRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Vehicle identification no.</FieldLabel>
                      <FieldInput value={fields.vehicle.vin} onChange={e => setVehicle('vin', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Licence plate no.</FieldLabel>
                      <FieldInput
                        value={fields.vehicle.license_plate || ''}
                        onChange={e => setVehicle('license_plate', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td className="small">
                      <FieldLabel>Year</FieldLabel>
                      <FieldInput value={fields.vehicle.year} onChange={e => setVehicle('year', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Model</FieldLabel>
                      <FieldInput value={fields.vehicle.model} onChange={e => setVehicle('model', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Make</FieldLabel>
                      <FieldInput value={fields.vehicle.make} onChange={e => setVehicle('make', e.target.value)} />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td colSpan={2} />
                    <td>
                      <FieldLabel>Odometer reading</FieldLabel>
                      <FieldInput
                        value={fields.vehicle.mileage}
                        onChange={e => setVehicle('mileage', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>On selling date</FieldLabel>
                      <FieldInput
                        value={fields.vehicle.selling_date}
                        onChange={e => setVehicle('selling_date', e.target.value)}
                      />
                    </td>
                    <td className="small-text">
                      I, AS SELLING PARTY OF THE VEHICLE DESCRIBED ABOVE, SOLD AND DELIVERED POSSESSION OF SAID
                      VEHICLE TO:
                    </td>
                  </RosRow>

                  <RosHeaderRow>
                    <td colSpan={3}>Buyer's information</td>
                  </RosHeaderRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Buyer's dealer name</FieldLabel>
                      <FieldInput
                        value={fields.buyer.dealership_name}
                        onChange={e => setBuyer('dealership_name', e.target.value)}
                      />
                    </td>
                    <td>
                      <TwoUp>
                        <div>
                          <FieldLabel>Buyer's dealer no.</FieldLabel>
                          <FieldInput
                            value={fields.buyer.number}
                            onChange={e => setBuyer('number', e.target.value)}
                          />
                        </div>
                        <div>
                          <FieldLabel>Seller permit number</FieldLabel>
                          <FieldInput
                            value={fields.buyer.seller_permit_number || ''}
                            onChange={e => setBuyer('seller_permit_number', e.target.value)}
                          />
                        </div>
                      </TwoUp>
                    </td>
                  </RosRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Buyer's contact name</FieldLabel>
                      <FieldInput value={fields.buyer.name} onChange={e => setBuyer('name', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Buyer's mobile no.</FieldLabel>
                      <FieldInput
                        value={fields.buyer.mobile_number || ''}
                        onChange={e => setBuyer('mobile_number', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Buyer's address</FieldLabel>
                      <FieldInput
                        value={fields.buyer.address}
                        onChange={e => setBuyer('address', e.target.value)}
                      />
                    </td>
                    <td>
                      <FieldLabel>Buyer's phone</FieldLabel>
                      <FieldInput
                        value={fields.buyer.phone_number}
                        onChange={e => setBuyer('phone_number', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td>
                      <FieldLabel>City</FieldLabel>
                      <FieldInput value={fields.buyer.city} onChange={e => setBuyer('city', e.target.value)} />
                    </td>
                    <td className="small">
                      <FieldLabel>State</FieldLabel>
                      <FieldInput value={fields.buyer.state} onChange={e => setBuyer('state', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Zip code</FieldLabel>
                      <FieldInput
                        value={fields.buyer.zip_code}
                        onChange={e => setBuyer('zip_code', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosHeaderRow>
                    <td colSpan={3}>Seller's information</td>
                  </RosHeaderRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Seller's dealer name</FieldLabel>
                      <FieldInput
                        value={fields.seller.dealership_name}
                        onChange={e => setSeller('dealership_name', e.target.value)}
                      />
                    </td>
                    <td>
                      <FieldLabel>Seller's dealer no.</FieldLabel>
                      <FieldInput value={fields.seller.number} onChange={e => setSeller('number', e.target.value)} />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Seller's contact name</FieldLabel>
                      <FieldInput value={fields.seller.name} onChange={e => setSeller('name', e.target.value)} />
                    </td>
                    <td />
                  </RosRow>

                  <RosRow>
                    <td colSpan={2}>
                      <FieldLabel>Seller's address</FieldLabel>
                      <FieldInput
                        value={fields.seller.address}
                        onChange={e => setSeller('address', e.target.value)}
                      />
                    </td>
                    <td>
                      <FieldLabel>Seller's phone</FieldLabel>
                      <FieldInput
                        value={fields.seller.phone_number}
                        onChange={e => setSeller('phone_number', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  <RosRow>
                    <td>
                      <FieldLabel>City</FieldLabel>
                      <FieldInput value={fields.seller.city} onChange={e => setSeller('city', e.target.value)} />
                    </td>
                    <td className="small">
                      <FieldLabel>State</FieldLabel>
                      <FieldInput value={fields.seller.state} onChange={e => setSeller('state', e.target.value)} />
                    </td>
                    <td>
                      <FieldLabel>Zip code</FieldLabel>
                      <FieldInput
                        value={fields.seller.zip_code}
                        onChange={e => setSeller('zip_code', e.target.value)}
                      />
                    </td>
                  </RosRow>

                  {showSellerFees && (
                    <RosRow>
                      <td colSpan={2} />
                      <td>
                        <FeeBox>
                          <FeeRow>
                            <FeeLabelSpan>Purchase Price</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.vehicle.price}
                              onChange={e => {
                                setVehicle('price', e.target.value);
                                recalcSellerTotal();
                              }}
                            />
                          </FeeRow>
                          <FeeRow>
                            <FeeLabelSpan>AutoAxess Sell Fee</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.seller.fee || ''}
                              onChange={e => {
                                setSeller('fee', e.target.value);
                                recalcSellerTotal();
                              }}
                            />
                          </FeeRow>
                          {fields.seller.additional_fees_total != null && (
                            <FeeRow>
                              <FeeLabelSpan>Additional Fees</FeeLabelSpan>
                              <FeeValueInput
                                value={fields.seller.additional_fees_total}
                                onChange={e => {
                                  setSeller('additional_fees_total', e.target.value);
                                  recalcSellerTotal();
                                }}
                              />
                            </FeeRow>
                          )}
                          <FeeTotalRow>
                            <FeeLabelSpan>Total</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.vehicle.totalWithoutFee || ''}
                              onChange={e => {
                                setVehicle('totalWithoutFee', e.target.value);
                                setTotalOverridden(true);
                              }}
                            />
                          </FeeTotalRow>
                        </FeeBox>
                      </td>
                    </RosRow>
                  )}

                  {showBuyerFees && (
                    <RosRow>
                      <td colSpan={2} />
                      <td>
                        <FeeBox>
                          <FeeRow>
                            <FeeLabelSpan>Purchase Price</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.vehicle.price}
                              onChange={e => {
                                setVehicle('price', e.target.value);
                                recalcBuyerTotal();
                              }}
                            />
                          </FeeRow>
                          <FeeRow>
                            <FeeLabelSpan>Buy Fee</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.processing_fee || ''}
                              onChange={e => {
                                setField('processing_fee', e.target.value);
                                recalcBuyerTotal();
                              }}
                            />
                          </FeeRow>
                          {fields.transportation_fee != null && (
                            <FeeRow>
                              <FeeLabelSpan>Transport</FeeLabelSpan>
                              <FeeValueInput
                                value={fields.transportation_fee}
                                onChange={e => {
                                  setField('transportation_fee', e.target.value);
                                  recalcBuyerTotal();
                                }}
                              />
                            </FeeRow>
                          )}
                          {fields.additional_fees_total != null && (
                            <FeeRow>
                              <FeeLabelSpan>Additional Fees</FeeLabelSpan>
                              <FeeValueInput
                                value={fields.additional_fees_total}
                                onChange={e => {
                                  setField('additional_fees_total', e.target.value);
                                  recalcBuyerTotal();
                                }}
                              />
                            </FeeRow>
                          )}
                          <FeeTotalRow>
                            <FeeLabelSpan>Total</FeeLabelSpan>
                            <FeeValueInput
                              value={fields.vehicle_with_buy_fee || ''}
                              onChange={e => {
                                setField('vehicle_with_buy_fee', e.target.value);
                                setTotalOverridden(true);
                              }}
                            />
                          </FeeTotalRow>
                        </FeeBox>
                      </td>
                    </RosRow>
                  )}

                  {showPurchasePriceOnly && (
                    <RosRow>
                      <td colSpan={2} />
                      <PurchasePriceCell as="td">
                        Purchase Price&nbsp;
                        <FieldInput
                          value={fields.vehicle.price}
                          onChange={e => setVehicle('price', e.target.value)}
                          style={{ display: 'inline-block', width: 120 }}
                        />
                      </PurchasePriceCell>
                    </RosRow>
                  )}
                </tbody>
              </RosTable>

              {reportType !== 'seller' && (
                <>
                  <PickupLabel>Pick Up Instructions</PickupLabel>
                  <PickupTextarea
                    value={fields.pickup_instruction ?? ''}
                    onChange={e => setField('pickup_instruction', e.target.value)}
                  />
                </>
              )}

              {fields.buyer_fee && (
                <BuyerFeeNote>
                  NOTE:&nbsp;
                  <strong>
                    $
                    <FieldInput
                      value={fields.buyer_fee}
                      onChange={e => setField('buyer_fee', e.target.value)}
                      style={{ display: 'inline-block', width: 80 }}
                    />
                  </strong>
                  &nbsp;Buy Fee charged to&nbsp;
                  {fields.buyer.dealership_name}
                </BuyerFeeNote>
              )}
              </FormFieldset>
            )}
          </ModalBody>
        </ModalContainer>
      </ScrollWrap>

      {sending && (
        <SendReportOfSaleModal
          vehicleId={vehicleId}
          reportType={reportType}
          versionId={currentVersionId}
          onClose={() => setSending(false)}
        />
      )}
    </FullScreen>,
    document.body
  );
};

export default EditReportOfSaleModal;
